import InfoIcon from '@material-design-icons/svg/filled/info.svg?react';
import type { ReactNode } from 'react';
import { toast } from 'sonner';
import { useArweaveUpload } from '../hooks/useArweaveUpload';
import { useMintFlow } from '../hooks/useMintFlow';
import { useTRPC } from '../hooks/useTRPC';
import { Button } from './Button';
import {
  NftMintConnectedWalletBlock,
  NftMintDisconnectedWalletPrompt,
  NftMintErrorBlock,
  NftMintModalFrame,
  NftMintRequiredWalletBlock,
  NftMintSuccessBlock,
  NftMintWrongWalletMessage,
} from './NftMintModalPresentation';

import { getErrorMessage } from '../utils/errorHandling';
import { addBreadcrumb } from '../utils/errorReporting';
import { mintAppNftWorkflow } from '../utils/nftMinting/mintNftWorkflow';
import { fetchAssetWithPresignedUrl } from '../utils/nftMintUtils';
import { SOLANA_NETWORK_LABEL } from '../utils/solanaUtils';

interface AppNftMintModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (mintAddress: string, txSignature: string) => void;
  appData: {
    id: string;
    name: string;
    iconUrl?: string;
    website?: string;
    androidPackage: string;
    publisherAddress: string;
  };
}

export function AppNftMintModal({
  isOpen,
  onClose,
  onSuccess,
  appData,
}: AppNftMintModalProps) {
  const {
    wallet,
    connected,
    publicKey,
    isLoading,
    setIsLoading,
    mintResult,
    lastError,
    retryCount,
    isCorrectWallet,
    validateWallet,
    handleMintError,
    handleMintSuccess,
  } = useMintFlow({
    isOpen,
    requiredWalletAddress: appData.publisherAddress,
  });
  const trpc = useTRPC();
  const { uploadAppNftData } = useArweaveUpload();

  const handleMint = async () => {
    if (
      !validateWallet({
        missingWalletMessage:
          'No app wallet address found. Please update your app configuration.',
        disconnectedWalletMessage: 'Please connect your wallet first',
        wrongWalletMessage: (walletAddress) =>
          `Please connect the app wallet: ${walletAddress}`,
      })
    ) {
      return;
    }

    if (!publicKey) {
      return;
    }

    setIsLoading(true);

    addBreadcrumb('App NFT mint initiated', 'nft-mint', {
      dappId: appData.id,
      appName: appData.name,
      androidPackage: appData.androidPackage,
      hasIconUrl: !!appData.iconUrl,
    });

    try {
      const balanceResult = await trpc.release.getWalletBalance.query({
        walletAddress: publicKey.toBase58(),
      });

      if (balanceResult._tag === 'Left') {
        throw new Error('Failed to check wallet balance');
      }

      const balance = balanceResult.right.balance;
      if (balance < 0.05) {
        throw new Error(
          'Insufficient SOL balance. You need at least 0.05 SOL to mint the NFT and cover the required on-chain transaction costs.',
        );
      }

      toast.info('Preparing to upload app metadata...');

      if (!appData.iconUrl) {
        throw new Error(
          'App icon is missing. Upload an icon in your dApp configuration before minting.',
        );
      }

      let iconFile: File;
      try {
        iconFile = await fetchAssetWithPresignedUrl(
          appData.iconUrl,
          'app-icon.png',
          'image/png',
          trpc,
        );
      } catch (error) {
        const message = getErrorMessage(error);
        throw new Error(`Failed to download app icon: ${message}`);
      }

      toast.info(
        'Preparing your App NFT upload. Your wallet may request a signature if the selected upload provider requires one.',
        { duration: 8000 },
      );
      let lastProgressMessage = '';
      const result = await mintAppNftWorkflow({
        wallet,
        trpc,
        uploadAppNftData,
        app: {
          dappId: appData.id,
          name: appData.name,
          website: appData.website,
          androidPackage: appData.androidPackage,
          publisherAddress: appData.publisherAddress,
        },
        iconFile,
        onStatus: (status) => {
          if (!status || status === lastProgressMessage) {
            return;
          }
          lastProgressMessage = status;
          toast.info(status, { duration: 2500 });
        },
        onUploadProgress: (_progress, message, isSignatureInProgress) => {
          const resolvedMessage = isSignatureInProgress
            ? 'Waiting for wallet signature...'
            : message;

          if (!resolvedMessage || resolvedMessage === lastProgressMessage) {
            return;
          }

          lastProgressMessage = resolvedMessage;
          toast.info(resolvedMessage, { duration: 2500 });
        },
      });

      handleMintSuccess({
        mintAddress: result.mintAddress,
        txSignature: result.transactionSignature,
        successMessage: 'App NFT minted successfully!',
        onSuccess,
        onClose,
      });
    } catch (error) {
      handleMintError(error, {
        getCaptureContext: ({ errorMessage, retryCount, walletAddress }) => ({
          operation: 'App NFT mint failed',
          component: 'AppNftMintModal',
          nft_type: 'app',
          stage: 'frontend_mint',
          dappId: appData.id,
          appName: appData.name,
          androidPackage: appData.androidPackage,
          publisherAddress: appData.publisherAddress,
          iconUrl: appData.iconUrl,
          walletConnected: !!publicKey,
          walletAddress,
          retryCount,
          errorMessage,
        }),
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <AppNftMintModalView
      appData={appData}
      isLoading={isLoading}
      mintResult={mintResult}
      lastError={lastError}
      retryCount={retryCount}
      connected={connected}
      connectedWalletAddress={publicKey?.toBase58() ?? null}
      isCorrectWallet={isCorrectWallet}
      onMint={handleMint}
      onClose={onClose}
    />
  );
}

interface AppNftMintModalViewProps {
  appData: AppNftMintModalProps['appData'];
  isLoading: boolean;
  mintResult: { mintAddress: string; txSignature: string } | null;
  lastError: string | null;
  retryCount: number;
  connected: boolean;
  connectedWalletAddress: string | null;
  isCorrectWallet: boolean;
  onMint: () => void;
  onClose: () => void;
  walletButton?: ReactNode;
}

export function AppNftMintModalView({
  appData,
  isLoading,
  mintResult,
  lastError,
  retryCount,
  connected,
  connectedWalletAddress,
  isCorrectWallet,
  onMint,
  onClose,
  walletButton,
}: AppNftMintModalViewProps) {
  return (
    <NftMintModalFrame
      title="Complete App NFT Minting"
      description={
        <>
          Your dApp was created successfully. Complete the NFT minting to enable
          on-chain publishing of releases.
        </>
      }
      isLoading={isLoading}
      onClose={onClose}
    >
      {mintResult ? (
        <NftMintSuccessBlock
          title="App NFT Minted Successfully!"
          description="Your App NFT has been created on the Solana blockchain."
          mintAddress={mintResult.mintAddress}
          txSignature={mintResult.txSignature}
        />
      ) : (
        <div className="space-y-4">
          <div className="p-4 bg-neutral-800 rounded-xl border border-neutral-700">
            <div className="text-sm font-medium text-neutral-300 mb-3">
              App NFT Details
            </div>

            <div className="space-y-2">
              <div className="flex justify-between text-xs">
                <span className="text-neutral-400">Name:</span>
                <span className="text-white">{appData.name}</span>
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-neutral-400">Package:</span>
                <span className="text-white font-mono">
                  {appData.androidPackage}
                </span>
              </div>
              {appData.website && (
                <div className="flex justify-between text-xs">
                  <span className="text-neutral-400">Website:</span>
                  <span className="text-white truncate max-w-[200px]">
                    {appData.website}
                  </span>
                </div>
              )}
              <div className="flex justify-between text-xs">
                <span className="text-neutral-400">Type:</span>
                <span className="text-white">App Collection</span>
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-neutral-400">Network:</span>
                <span className="text-white">
                  Solana {SOLANA_NETWORK_LABEL}
                </span>
              </div>
            </div>
          </div>

          <NftMintRequiredWalletBlock
            walletAddress={appData.publisherAddress}
          />

          {connected && connectedWalletAddress && (
            <NftMintConnectedWalletBlock
              walletAddress={connectedWalletAddress}
              isCorrectWallet={isCorrectWallet}
              wrongWalletMessage="Please connect the app wallet to mint the App NFT"
              walletButton={walletButton}
            />
          )}

          <div className="flex items-start gap-3 p-4 bg-blue-900/20 rounded-xl border border-blue-700/30">
            <InfoIcon className="size-4 fill-white flex-shrink-0 mt-0.5" />
            <div className="text-xs text-blue-300">
              <p>
                The App NFT minting was interrupted. To complete it, you'll need
                to:
              </p>
              <ol className="mt-1 space-y-1 list-decimal list-inside">
                <li>
                  Authorize the configured upload provider if your wallet
                  prompts for it
                </li>
                <li>Approve the transaction in your wallet</li>
                <li>
                  Pay network fees and rent (≈0.02 SOL plus any upload provider
                  costs)
                </li>
                <li>Wait for blockchain confirmation</li>
              </ol>
              <p className="mt-2">
                Note: Your dApp has been created and you can use it without the
                NFT, but NFT minting is required for publishing releases
                on-chain.
              </p>
            </div>
          </div>

          {lastError && (
            <NftMintErrorBlock error={lastError} retryCount={retryCount} />
          )}

          <div className="flex flex-col gap-2">
            <Button
              onClick={onMint}
              disabled={!connected || !isCorrectWallet || isLoading}
              submitting={isLoading}
              className="w-full"
            >
              {isLoading
                ? 'Minting NFT...'
                : lastError && retryCount > 0
                  ? 'Retry Mint'
                  : 'Complete NFT Minting'}
            </Button>

            {!connected && (
              <NftMintDisconnectedWalletPrompt
                message="Please connect the app wallet to mint the App NFT"
                walletButton={walletButton}
              />
            )}

            {connected && !isCorrectWallet && <NftMintWrongWalletMessage />}

            <Button
              variant="secondary"
              onClick={onClose}
              disabled={isLoading}
              className="w-full"
            >
              Skip for Now
            </Button>
          </div>
        </div>
      )}
    </NftMintModalFrame>
  );
}
